import { Building2, MapPin, Users } from "lucide-react";
import type { Parcel, ParcelOwner } from "../../types";

const parcelNumber = (p: Parcel): string => p.short_number || p.teryt;

const ownerName = (o: ParcelOwner): string => (o.full_name || o.office_name || "").trim() || "—";

/** Rola tylko wtedy, gdy to NIE zwykły właściciel (pusta rola = właściciel) — np.
 *  użytkownik wieczysty obok gminy-właściciela gruntu. */
const ownerRole = (o: ParcelOwner): string =>
  o.role && o.role !== "owner" ? (o.role_display || "").trim() : "";

/** Udział samodzielny + udział we wspólności (wypis potrafi wykazać jedną osobę dwa razy). */
const ownerShare = (o: ParcelOwner): string =>
  [o.own_share, o.ownership_share].map((s) => (s ?? "").trim()).filter(Boolean).join(" + ");

const ownerAddress = (o: ParcelOwner): string =>
  (o.kind === "company" && o.office ? o.office_full_address : o.full_address || o.address)?.trim() || "";

/** One owner of a private parcel: name, role (if not plain owner), share, address. */
function OwnerItem({ owner: o }: { owner: ParcelOwner }) {
  const role = ownerRole(o);
  const share = ownerShare(o);
  const address = ownerAddress(o);
  return (
    <span
      className="inline-flex min-w-0 max-w-full items-baseline gap-1.5"
      title={[ownerName(o), role, share && `udział ${share}`, address].filter(Boolean).join(" · ")}
    >
      <span className="font-semibold text-[var(--color-text-primary)]">{ownerName(o)}</span>
      {o.marital_group && (
        <span className="text-[var(--color-text-muted)]" title="Wspólność małżeńska (z wypisu)">⚭</span>
      )}
      {role && <span className="text-[10px] text-sky-700">{role.toLowerCase()}</span>}
      {share && <span className="font-mono text-[10px] text-[var(--color-text-secondary)]">{share}</span>}
      {address && (
        <span className="truncate text-[10px] text-[var(--color-text-muted)]">{address}</span>
      )}
    </span>
  );
}

/** Plan-editor header strip: WHICH działka(i) the sheet set is for and WHOSE land it is.
 *  Several sets are created at once (one per parcel / one per urząd) and their editors
 *  looked the same — this names the parcel and its owners (private set), or the parcels
 *  and their owning offices (office set). `parcels` = the set's parcels resolved from the
 *  project list; `fallbackLabel` covers a parcel not (yet) in that list. */
export function SheetSetParcelsInfo({
  mode,
  parcels,
  fallbackLabel,
  loading,
}: {
  mode: "parcel" | "parcel_office";
  parcels: Parcel[];
  fallbackLabel: string | null;
  loading: boolean;
}) {
  if (mode === "parcel") {
    const p = parcels[0];
    const details = p
      ? [
          p.short_number ? p.teryt : "",
          (p.obreb ?? "").trim() ? `obr. ${p.obreb.trim()}` : "",
          (p.commune ?? "").trim() ? `gm. ${p.commune.trim()}` : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : "";
    const owners = p?.owners ?? [];
    return (
      <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2 space-y-1">
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sky-900">
          <MapPin size={14} className="shrink-0 self-center text-sky-700" />
          <span className="text-sm font-semibold">
            Działka {p ? parcelNumber(p) : (fallbackLabel ?? "?")}
          </span>
          {details && <span className="text-[11px] font-mono text-sky-800/80">{details}</span>}
        </p>
        <div className="flex items-start gap-2 text-[11px]">
          <Users size={13} className="mt-[2px] shrink-0 text-sky-700" />
          {loading ? (
            <span className="text-[var(--color-text-muted)]">Wczytywanie właścicieli…</span>
          ) : !p ? (
            <span className="text-amber-700">Działki nie ma na liście działek projektu — brak danych o właścicielu.</span>
          ) : owners.length === 0 ? (
            <span className="text-amber-700">Brak właścicieli — działka bez wypisu.</span>
          ) : (
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="text-[var(--color-text-muted)]">
                {owners.length === 1 ? "Właściciel:" : `Właściciele (${owners.length}):`}
              </span>
              {owners.map((o) => (
                <OwnerItem key={o.id} owner={o} />
              ))}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Office set: owners aggregated across its parcels — one entry per urząd / owner, with
  // the parcels it holds (co-owned parcels list both owners).
  const byOwner = new Map<string, { name: string; role: string; numbers: string[] }>();
  let withoutOwners = 0;
  for (const p of parcels) {
    const owners = p.owners ?? [];
    if (owners.length === 0) withoutOwners += 1;
    for (const o of owners) {
      const key = o.office ? `office:${o.office}:${o.role}` : `name:${ownerName(o)}:${o.role}`;
      const entry = byOwner.get(key) ?? { name: ownerName(o), role: ownerRole(o), numbers: [] };
      if (!entry.numbers.includes(parcelNumber(p))) entry.numbers.push(parcelNumber(p));
      byOwner.set(key, entry);
    }
  }
  const numbers = parcels.map(parcelNumber);
  const SHOWN = 20;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-1">
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-amber-900" title={numbers.join(", ")}>
        <MapPin size={14} className="shrink-0 self-center text-amber-700" />
        <span className="text-sm font-semibold">
          {loading ? "Działki…" : parcels.length > 0 ? `Działki (${parcels.length}):` : "Działki: brak danych"}
        </span>
        <span className="text-[11px] font-mono">
          {numbers.slice(0, SHOWN).join(", ")}
          {numbers.length > SHOWN ? ` +${numbers.length - SHOWN}` : ""}
        </span>
      </p>
      {!loading && (
        <div className="flex items-start gap-2 text-[11px]">
          <Building2 size={13} className="mt-[2px] shrink-0 text-amber-700" />
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <span className="text-[var(--color-text-muted)]">
              {byOwner.size === 1 ? "Właściciel:" : `Właściciele (${byOwner.size}):`}
            </span>
            {[...byOwner.entries()].map(([key, e]) => (
              <span key={key} className="inline-flex items-baseline gap-1.5" title={`dz. ${e.numbers.join(", ")}`}>
                <span className="font-semibold text-[var(--color-text-primary)]">{e.name}</span>
                {e.role && <span className="text-[10px] text-amber-800">{e.role.toLowerCase()}</span>}
                <span className="font-mono text-[10px] text-[var(--color-text-muted)]">{e.numbers.length} dz.</span>
              </span>
            ))}
            {withoutOwners > 0 && (
              <span className="text-amber-700">bez wypisu: {withoutOwners} dz.</span>
            )}
            {byOwner.size === 0 && withoutOwners === 0 && (
              <span className="text-[var(--color-text-muted)]">—</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
